import React, { useMemo, useState } from 'react';

import { Chip, FormHelperText, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import type { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import type { RopaCategory } from 'config/api/processingActivities/processingActivities.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import sortRopaCategories from 'pages/ProcessingActivities/_shared/sortRopaCategories';

import type { ProcessingActivityEditorCategory, ProcessingActivityEditorFormValues } from '../processingActivityEditor.types';

type CategoryOption = {
  id?: number;
  name: string;
  inputValue?: string;
};

type ProcessingActivityCategoryFieldProps = {
  error?: string;
  fieldName: 'dataSubjectCategories' | 'personalDataCategories' | 'recipientCategories';
  formik: FormikProps<ProcessingActivityEditorFormValues>;
  label: string;
  ropaCategories: RopaCategory[];
};

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    marginTop: theme.spacing(0.5),
  },
}));

const normalizeCategoryName = (name: string) => name.trim();

const getCategoryOptionLabel = (option: CategoryOption | string): string => {
  if (typeof option === 'string') {
    return option;
  }

  return option.inputValue ?? option.name ?? '';
};

const ProcessingActivityCategoryField: React.FC<ProcessingActivityCategoryFieldProps> = ({
  error,
  fieldName,
  formik,
  label,
  ropaCategories,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [inputValue, setInputValue] = useState('');
  const selectedCategories = formik.values[fieldName];

  const sortedSelectedCategories = useMemo(() => sortRopaCategories(selectedCategories), [selectedCategories]);

  const options = useMemo<CategoryOption[]>(
    () =>
      sortRopaCategories(
        ropaCategories.map(category => ({
          id: category.id,
          name: category.name,
        })),
      ),
    [ropaCategories],
  );

  const commitCategory = (rawName: string) => {
    const name = normalizeCategoryName(rawName);
    if (!name) return;

    const existing = selectedCategories.find(category => category.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      setInputValue('');
      return;
    }

    const matchedOption = ropaCategories.find(category => category.name.toLowerCase() === name.toLowerCase());
    const nextCategory: ProcessingActivityEditorCategory = matchedOption ? { id: matchedOption.id, name: matchedOption.name } : { name };

    formik.setFieldValue(fieldName, sortRopaCategories([...selectedCategories, nextCategory]));
    setInputValue('');
  };

  const removeCategory = (name: string) => {
    formik.setFieldValue(
      fieldName,
      selectedCategories.filter(category => category.name !== name),
    );
  };

  const handleAutocompleteChange = (_event: unknown, value: CategoryOption | null) => {
    if (!value) return;

    if (value.inputValue) {
      commitCategory(value.inputValue);
      return;
    }

    commitCategory(value.name);
  };

  return (
    <div className={styles.root}>
      {sortedSelectedCategories.length > 0 && (
        <div className={styles.chips}>
          {sortedSelectedCategories.map(category => (
            <Chip key={category.name} label={category.name} onDelete={() => removeCategory(category.name)} size='small' />
          ))}
        </div>
      )}
      <Autocomplete
        clearOnBlur
        getOptionLabel={getCategoryOptionLabel}
        handleHomeEndKeys
        inputValue={inputValue}
        onChange={handleAutocompleteChange}
        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
        options={options.filter(
          option => !selectedCategories.some(category => category.name.toLowerCase() === getCategoryOptionLabel(option).toLowerCase()),
        )}
        renderInput={params => (
          <TextField
            {...params}
            error={Boolean(error)}
            id={`processing-activity-${fieldName}`}
            label={label}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                commitCategory(inputValue);
              }
            }}
            placeholder={t(processing_activities_messages.editor.fields.category_placeholder)}
            variant='outlined'
          />
        )}
        value={null}
      />
      {error ? (
        <FormHelperText error>{error}</FormHelperText>
      ) : (
        <div className={styles.helperText}>{t(processing_activities_messages.editor.fields.category_help)}</div>
      )}
    </div>
  );
};

export default ProcessingActivityCategoryField;
